import { useNavigate } from "react-router-dom";
import { getUnixTime } from "date-fns";
import { useAppBridge } from "@shopify/app-bridge-react";
// form
import { object, string } from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
// @polaris
import { Button, Box, Modal } from "@shopify/polaris";
// components
import { FormProvider } from "../../../components/hook-form";
import { RHFTextField } from "../../../components/hook-form/v2";
import { Stack } from "../../../components";
// constants
import { APP_ROUTES, PAGE_MODES, defaultSellingPlan } from "../../../common/constants";
// hooks
import useLocales from "../../../hooks/useLocales";
import useSellingPlansContext from "../../../hooks/useSellingPlansContext";

const NewSellingPlanForm = ({ handleOnClose }) => {
  const { createPlan } = useSellingPlansContext();

  const navigate = useNavigate();
  const shopify = useAppBridge();

  const { translate } = useLocales();

  const SellingPlanSchema = object().shape({
    name: string().max(200).required(translate("messages.selling-plans.plan-name-is-required")),
  });

  const methods = useForm({
    resolver: yupResolver(SellingPlanSchema),
    defaultValues: {
      name: "",
    },
  });

  const onSubmit = async (data) => {
    try {
      handleOnClose();

      const { sellingPlanGroupId } = await createPlan({
        name: data.name,
        description: defaultSellingPlan.description,
        sellingPlanName: defaultSellingPlan.sellingPlanName,
        sellingPlanOptions: defaultSellingPlan.sellingPlanOptions,
        settings: {
          billingPolicy: {
            ...defaultSellingPlan.billingPolicy,
            finalPayment: {
              ...defaultSellingPlan.billingPolicy.finalPayment,
              date: getUnixTime(defaultSellingPlan.billingPolicy.finalPayment.date),
            },
          },
          shippingDate: {
            ...defaultSellingPlan.shippingDate,
            date: getUnixTime(defaultSellingPlan.shippingDate.date),
          },
          discount: defaultSellingPlan.discount,
          discountType: defaultSellingPlan.discountType,
          inventoryPolicy: defaultSellingPlan.inventoryPolicy,
        },
      });

      navigate(`${APP_ROUTES.SETTINGS.SELLING_PLANS}/${sellingPlanGroupId}`, {
        state: { mode: PAGE_MODES.CREATE },
      });
    } catch (error) {
      shopify.toast.show(`${translate("messages.error-occured")}`);
    }
  };

  const {
    watch,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const values = watch();

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Modal.Section>
        <Stack flexDirection="column" spacing={2}>
          <RHFTextField
            name="name"
            label={translate("page.selling-plans.create-plan-label")}
            placeholder={translate("page.selling-plans.create-plan-placeholder")}
          />
        </Stack>
      </Modal.Section>

      <Box minHeight="61px" padding="400" display="flex">
        <Stack alignItems="center" justifyContent="flex-end" spacing={1}>
          <Button onClick={handleOnClose}>{translate("common.cancel")}</Button>

          <Button variant="primary" loading={isSubmitting} submit disabled={!values.name.length}>
            {translate("page.selling-plans.create-plan")}
          </Button>
        </Stack>
      </Box>
    </FormProvider>
  );
};

export default NewSellingPlanForm;
